/**
 * CARDS THAT FOLD, SO A TAB IS A TAB AND NOT A PAGE.
 *
 * Anthony, 2026-09-09: "take a look at how to better organize the areas
 * beneath the six tile function on each page. They are very spread out and
 * hard to get to. We'd like less scroll if possible to compartmentalize."
 *
 * A panel under the six tabs is a stack of cards, and some of those cards
 * are long: the whole authorization record with the signatures drawn out,
 * every event that has ever happened, every other job this customer has
 * had. All of it worth keeping and none of it worth scrolling past to
 * reach the thing underneath.
 *
 * ─── WHY FOLDING RATHER THAN MOVING ────────────────────────────────────
 *
 * The obvious fix is more tabs, and it is the wrong one. People have
 * learned where things are, and a seventh tab moves things that are
 * currently found. Folding leaves everything exactly where it was and only
 * changes how much of it is unrolled -- and the card heads already carry
 * their counts ("What they authorized · 2"), so a closed card still
 * answers the question most people came to ask.
 *
 * ─── AND WHY IT REMEMBERS ──────────────────────────────────────────────
 *
 * An advisor who opens the authorizations on every job is not asking to
 * open them again tomorrow. The choice is kept per card, per person, in
 * their own browser -- a preference, not data, so localStorage is right
 * and a private window losing it costs nothing.
 */

const KEY = "fold:";

/** Reading a browser's storage can throw outright. It never matters here. */
const remembered = (id: string): boolean | null => {
  try {
    const v = localStorage.getItem(KEY + id);
    return v === null ? null : v === "1";
  } catch { return null; }
};

const remember = (id: string, open: boolean): void => {
  try { localStorage.setItem(KEY + id, open ? "1" : "0"); } catch { /* fine */ }
};

/**
 * Turn every `[data-fold]` card in scope into one that opens and closes.
 *
 * `data-fold="open"` starts unrolled the first time somebody sees it;
 * anything else starts closed. After that the person's own choice wins.
 *
 * The card keeps its markup. This only adds a button around the head and a
 * class on the card, so nothing that queries inside these cards breaks --
 * including the wiring that runs after it, because a closed card's
 * contents are still in the document and still findable.
 */
export function foldCards(scope: ParentNode = document): void {
  scope.querySelectorAll<HTMLElement>("[data-fold]").forEach((card) => {
    const head = card.querySelector<HTMLElement>(".card-head");
    if (!head || card.classList.contains("foldable")) return;

    /* Named by what the card says rather than by position, so adding a
       card above it does not shuffle everybody's remembered choices. */
    const id = (head.textContent ?? "").trim().split("·")[0]!.trim().slice(0, 40);
    const startOpen = card.dataset["fold"] === "open";
    /* `data-fold-fresh` opts a card out of remembering. A rental trip opens
       the section its lane needs (the deposit at Inspection, the return
       walk round while it is out), and a choice remembered from another
       trip in another lane would hide the one thing this trip is waiting
       on. Anthony, 2026-09-28, on the trip page's boxes. */
    const fresh = card.hasAttribute("data-fold-fresh");
    const open = (fresh ? null : remembered(id)) ?? startOpen;

    card.classList.add("foldable");
    card.classList.toggle("shut", !open);

    /* A button rather than a click handler on the div: it is a control,
       so it should be reachable with a keyboard and say so to a reader. */
    const btn = document.createElement("button");
    btn.className = "foldbtn";
    btn.type = "button";
    btn.setAttribute("aria-expanded", String(open));
    btn.innerHTML = `<span class="foldlab">${head.innerHTML}</span>
      <span class="foldarrow" aria-hidden="true"></span>`;
    head.innerHTML = "";
    head.appendChild(btn);

    btn.addEventListener("click", () => {
      const nowOpen = card.classList.contains("shut");
      card.classList.toggle("shut", !nowOpen);
      btn.setAttribute("aria-expanded", String(nowOpen));
      if (!fresh) remember(id, nowOpen);
    });
  });
}
